/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useMemo } from "react";

import { IBlueprintSchemaOpts } from "@adapptio/lib-blueprint";
import { useTranslate, useUuid } from "@adapptio/lib-components";
import {
	FieldLabel,
	FieldModeButton,
	FieldRow,
	FieldSection,
	FieldSectionContent
} from "@adapptio/lib-editor-ui";
import { useBlueprintModel, useMultipleBlueprintModels } from "../../hooks/useBlueprintModel";
import { FieldGroup } from "../Fields/FieldGroup";
import { FieldHeader } from "../Fields/FieldHeader";
import { FieldHeaderButton } from "../Fields/FieldHeaderButton";
import { FieldType } from "../Fields/FieldType";
import { getInspectorControls, getInspectorControlValueTypes } from "./Controls/inspectorControls";
import { IInspectorControlProps, IInspectorControls } from "./InspectorTypes";
import { InvalidModelNode } from "./InvalidModelNode";
import { InspectorControl } from "./InspectorControl";
import { hasAnyInspectorControls } from "./InspectorHelpers";

const emptyArray = [];

export interface IInspectModelProps extends IInspectorControlProps {
	/** Allows to override specific model controls */
	overrideControls?: Partial<IInspectorControls>;
	/** Overrides provided schema options */
	overrideSchemaOpts?: IBlueprintSchemaOpts;
	/** Fallback schema options (used when model schema has no label, description, etc. configured) */
	fallbackSchemaOpts?: IBlueprintSchemaOpts;
}

export const InspectModel: FC<IInspectModelProps> = ({
	model: _model,
	depth,
	path,
	fallbackSchemaOpts,
	overrideControls,
	overrideSchemaOpts
}) => {
	const t = useTranslate();

	// Bind root model
	const { model } = useBlueprintModel(_model);

	// Get controls
	const controls: IInspectorControls = {
		...getInspectorControls(_model),
		...overrideControls
	};

	// Bind dependency models if available
	useMultipleBlueprintModels(controls.dependencyModels || emptyArray);

	// Get value types
	const valueTypes = useMemo(() => {
		if (!controls.valueTypes || !controls.valueTypes?.model) {
			return emptyArray;
		}

		return controls.valueTypes.model
			? getInspectorControlValueTypes(controls.valueTypes?.model?.schema)
			: emptyArray;
	}, [ controls.valueTypes, controls.valueTypes?.model ]);

	const handleTypeChange = useCallback(
		(value: string) => {
			if (controls.valueTypes?.setCurrentType && controls.valueTypes?.model) {
				controls.valueTypes.setCurrentType(controls.valueTypes.model, value);
			}
		},
		[ controls.valueTypes?.setCurrentType, controls.valueTypes?.model ]
	);

	const schema = model?.schema;
	const schemaOpts = overrideSchemaOpts || schema?.opts || fallbackSchemaOpts;

	const id = useUuid();
	const label = schemaOpts?.label || fallbackSchemaOpts?.label;
	const translatedLabel = t("editor", label);

	const inspectorControlProps = useMemo<Pick<IInspectorControlProps, "id" | "label">>(
		() => ({
			id,
			label: translatedLabel
		}),
		[ translatedLabel ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	if (!hasAnyInspectorControls(controls)) {
		return null;
	}

	// Get header descriptive values
	const description =
		schemaOpts.description || (!schemaOpts.label ? fallbackSchemaOpts?.description : undefined);

	const childDepth =
		controls.displayInSection && schemaOpts.editorOptions?.layoutType !== "passthrough"
			? depth + 1
			: depth;

	const showTypesInHeader = !controls.primary && valueTypes.length > 0;

	const fieldModeComponent = valueTypes.length > 0 ? null : null;
	const labelComponent = label ? <FieldLabel forId={id}>{translatedLabel}</FieldLabel> : null;
	const primaryControlComponent = controls.primary ? (
		<InspectorControl config={controls.primary} path={path} depth={depth} {...inspectorControlProps} />
	) : null;
	const childControlComponent = controls.children ? (
		<InspectorControl
			config={controls.children}
			path={path}
			depth={childDepth}
			{...inspectorControlProps}
		/>
	) : null;

	// Define header
	const header =
		(label || description || controls.headerPrefix || controls.headerControls || showTypesInHeader) &&
		schemaOpts.editorOptions?.layoutType !== "noHeader" ? (
			<FieldHeader>
				{showTypesInHeader ? (
					<FieldType
						options={valueTypes}
						value={controls.valueTypes.getCurrentType(controls.valueTypes.model)}
						tooltip="Switch between value types."
						onChange={handleTypeChange}
						inline
					/>
				) : null}
				{controls.headerPrefix ? (
					<InspectorControl config={controls.headerPrefix} path={path} depth={depth} />
				) : null}
				{labelComponent}
				{controls.headerControls ? (
					<InspectorControl config={controls.headerControls} path={path} depth={depth} />
				) : null}
				{description ? (
					<FieldHeaderButton icon="mdi/help-circle" tooltip={t("editor", description)} />
				) : null}
			</FieldHeader>
		) : null;

	// Define content
	const content = (
		<>
			{controls.primary ? (
				<FieldRow>
					<FieldGroup grow={true}>
						{valueTypes.length > 0 ? (
							<FieldType
								options={valueTypes}
								value={controls.valueTypes.getCurrentType(controls.valueTypes.model)}
								tooltip="Switch between value types."
								onChange={handleTypeChange}
							/>
						) : null}
						<InspectorControl
							config={controls.primary}
							path={path}
							depth={depth}
							{...inspectorControlProps}
						/>
					</FieldGroup>
				</FieldRow>
			) : null}
			{controls.children ? (
				<InspectorControl
					config={controls.children}
					path={path}
					depth={childDepth}
					{...inspectorControlProps}
				/>
			) : null}
		</>
	);

	const primaryChildren = controls.primaryChildren ? (
		<InspectorControl
			config={controls.primaryChildren}
			path={path}
			depth={childDepth}
			{...inspectorControlProps}
		/>
	) : null;

	if (controls.displayInSection && schemaOpts.editorOptions?.layoutType !== "passthrough") {
		return (
			<FieldSection
				collapsible={!!header}
				defaultCollapsed={!(controls.sectionDefaultExpanded ?? true)}
				stateKey={path.join(".")}
			>
				<FieldRow>
					{labelComponent}
					{fieldModeComponent}
				</FieldRow>

				<FieldSectionContent disablePadding={depth < 1}>
					{primaryChildren}
					{content}
				</FieldSectionContent>
			</FieldSection>
		);
	} else {
		return (
			<>
				<FieldRow>
					{schemaOpts.editorOptions?.layoutType !== "passthrough" ? labelComponent : null}
					{primaryControlComponent}
					{fieldModeComponent}
				</FieldRow>

				{primaryChildren}
				{childControlComponent}
			</>
		);
	}
};
